/**
 * Health Score Cards Component
 *
 * Displays health score cards for each bot, showing grade and score breakdown.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Bot, Search, Cpu } from 'lucide-react';
import { BotHealthScore } from '@/services/visibilityDiagnosisService';

interface HealthScoreCardsProps {
  healthScores: Record<string, BotHealthScore>;
}

const gradeColors: Record<string, string> = {
  A: 'bg-green-500',
  B: 'bg-lime-500',
  C: 'bg-yellow-500',
  D: 'bg-orange-500',
  F: 'bg-red-500',
};

const gradeTextColors: Record<string, string> = {
  A: 'text-green-700 dark:text-green-400',
  B: 'text-lime-700 dark:text-lime-400',
  C: 'text-yellow-700 dark:text-yellow-400',
  D: 'text-orange-700 dark:text-orange-400',
  F: 'text-red-700 dark:text-red-400',
};

export function HealthScoreCards({ healthScores }: HealthScoreCardsProps) {
  const scores = Object.values(healthScores);

  // Separate AI bots and search bots
  const aiBots = scores.filter(s => s.is_ai_bot).sort((a, b) => b.total_score - a.total_score);
  const searchBots = scores.filter(s => !s.is_ai_bot).sort((a, b) => b.total_score - a.total_score);

  const renderScoreCard = (score: BotHealthScore) => (
    <Card key={score.bot_name} className="relative overflow-hidden">
      <div className={`absolute top-0 right-0 w-16 h-16 ${gradeColors[score.grade]} opacity-10 rounded-bl-full`} />
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            {score.is_ai_bot ? <Cpu className="h-4 w-4" /> : <Search className="h-4 w-4" />}
            {score.display_name}
          </CardTitle>
          <div className={`text-2xl font-bold ${gradeTextColors[score.grade]}`}>
            {score.grade}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold">{score.total_score}</span>
            <span className="text-muted-foreground text-sm">/ {score.max_score}</span>
          </div>

          <Progress value={score.total_score} className="h-2" />

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Session Depth</span>
              <span>{score.breakdown.session_depth.score}/{score.breakdown.session_depth.max}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Coverage</span>
              <span>{score.breakdown.coverage.score}/{score.breakdown.coverage.max}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Entry Diversity</span>
              <span>{score.breakdown.entry_diversity.score}/{score.breakdown.entry_diversity.max}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Not Blocked</span>
              <span>{score.breakdown.blocking_rules.score}/{score.breakdown.blocking_rules.max}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* AI Bots Section */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Cpu className="h-5 w-5" />
          AI Bot Health Scores
          <Badge variant="outline" className="ml-2">{aiBots.length} bots</Badge>
        </h3>
        {aiBots.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {aiBots.map(renderScoreCard)}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No AI bot data available</p>
        )}
      </div>

      {/* Search Bots Section */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Search className="h-5 w-5" />
          Search Engine Health Scores
          <Badge variant="outline" className="ml-2">{searchBots.length} bots</Badge>
        </h3>
        {searchBots.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {searchBots.slice(0, 8).map(renderScoreCard)}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No search bot data available</p>
        )}
      </div>
    </div>
  );
}
